import { useEffect, useMemo, useState, useCallback } from 'react';
import { GitBranch, AlertTriangle, Clock, CheckCircle2, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import { Table, Pagination, Badge, Checkbox, Autocomplete } from '@anyemp/ui-kit';
import { playbooks as playbooksApi, users as usersApi } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import StatCard from '../components/Dashboard/StatCard';
import ProfileLink from '../components/UI/ProfileLink';
import { PAGINATION_OPTIONS } from '../utils/paginationConstants';
import {
  getTalentTypeIcon,
  getTalentTypeLabel,
  getTalentTypeColor,
} from '../utils/talentTypeUtils';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import DataViewToggle from '../components/UI/DataViewToggle';
import { RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';

const TALENT_TYPE_OPTIONS = [
  { value: 'candidate', label: 'Candidate' },
  { value: 'trainee', label: 'Trainee' },
  { value: 'employee', label: 'Employee' },
  { value: 'presale', label: 'Presale' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysLeftOf = (endsAt) => {
  if (!endsAt) return null;
  const end = new Date(endsAt);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((end - today) / 86400000);
};

const ProgressBar = ({ percent, isOverdue }) => {
  const pct = Math.max(0, Math.min(100, percent || 0));
  const fillColor = isOverdue
    ? 'bg-error dark:bg-error'
    : pct >= 100
      ? 'bg-success dark:bg-success'
      : 'bg-primary dark:bg-primary';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-light-border-primary/60 dark:bg-dark-border-primary/60 rounded-full overflow-hidden min-w-[60px] ring-1 ring-light-border-primary dark:ring-dark-border-primary">
        <div className={`h-full ${fillColor} transition-all duration-300`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary w-10 text-right tabular-nums">
        {pct}%
      </span>
    </div>
  );
};

const DaysLeftCell = ({ days, isOverdue }) => {
  if (isOverdue || (days !== null && days < 0)) {
    return <Badge label={`${Math.abs(days)}d overdue`} icon={<AlertTriangle className="h-3 w-3" />} color="error" variant="subtle" size="small" />;
  }
  if (days === null) return <span className="text-xs text-light-text-tertiary">—</span>;
  if (days === 0) return <Badge label="today" color="warning" variant="subtle" size="small" />;
  if (days <= 7) return <Badge label={`${days}d left`} color="warning" variant="subtle" size="small" />;
  return <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">{days}d left</span>;
};

const TalentCell = ({ inst }) => {
  const talent = inst.talent || {};
  const TypeIcon = getTalentTypeIcon(talent.talent_type);
  const fullName = talent.first_name || talent.last_name
    ? `${talent.last_name || ''} ${talent.first_name || ''}`.trim()
    : `Talent #${inst.talent_id}`;

  return (
    <div className="flex flex-col items-start gap-1 min-w-[180px]">
      <ProfileLink
        to={`/talents/${inst.talent_id}#stages`}
        ariaLabel={`Open profile of ${fullName}`}
        className="inline-block max-w-full break-words"
      >
        {fullName}
      </ProfileLink>
      {talent.talent_type && (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${getTalentTypeColor(talent.talent_type)}`}>
          <TypeIcon className="h-3 w-3" />
          {getTalentTypeLabel(talent.talent_type)}
        </span>
      )}
    </div>
  );
};

const StagesDashboard = () => {
  const { hasPermission } = useAuth();

  const allowedTalentTypes = useMemo(() => (
    TALENT_TYPE_OPTIONS.filter((t) => hasPermission('talent-service', t.value, 'list')).map((t) => t.value)
  ), [hasPermission]);

  const [listLoading, setListLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1, limit: 20 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [playbookOptions, setPlaybookOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);

  const [selectedPlaybookIds, setSelectedPlaybookIds] = useState([]);
  const [selectedManagerIds, setSelectedManagerIds] = useState([]);
  const [selectedTalentTypes, setSelectedTalentTypes] = useState([]);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('stages-dashboard');

  useEffect(() => {
    (async () => {
      try {
        const [pbRes, usersRes] = await Promise.all([
          playbooksApi.list({ isActive: true }),
          usersApi.getAllUsers(),
        ]);
        setPlaybookOptions((pbRes?.data || []).map((p) => ({ value: String(p.id), label: p.name })));
        setManagerOptions((usersRes?.data?.users || []).map((u) => ({
          value: String(u.id),
          label: u.name || u.fullName || u.email || String(u.id),
        })));
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    })();
  }, []);

  const filterParams = useMemo(() => {
    const params = {};
    if (selectedPlaybookIds.length) params.playbookIds = selectedPlaybookIds.join(',');
    if (selectedManagerIds.length) params.managerIds = selectedManagerIds.join(',');
    if (selectedTalentTypes.length) params.talentTypes = selectedTalentTypes.join(',');
    else if (allowedTalentTypes.length) params.talentTypes = allowedTalentTypes.join(',');
    if (overdueOnly) params.overdueOnly = 'true';
    return params;
  }, [selectedPlaybookIds, selectedManagerIds, selectedTalentTypes, allowedTalentTypes, overdueOnly]);

  // Stable JSON key — recreating filterParams object should not refetch if values match
  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);

  // Stat cards summarize ALL active stages within the user's permission scope —
  // they intentionally ignore the page filters (playbook/manager/type/overdue).
  const statsParams = useMemo(() => {
    const params = {};
    if (allowedTalentTypes.length) params.talentTypes = allowedTalentTypes.join(',');
    return params;
  }, [allowedTalentTypes]);

  const statsKey = useMemo(() => JSON.stringify(statsParams), [statsParams]);

  const fetchList = useCallback(async (p, lim, params) => {
    setListLoading(true);
    try {
      const res = await playbooksApi.listAllInstances({ ...params, status: 'active', page: p, limit: lim });
      setRows(res?.data || []);
      setPagination(res?.pagination || { total: 0, page: 1, totalPages: 1, limit: lim });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load stages');
    } finally {
      setListLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async (params) => {
    setStatsLoading(true);
    try {
      const res = await playbooksApi.dashboardStats(params);
      setStats(res?.data || {});
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Stats depend only on the permission scope, not the page filters.
  useEffect(() => {
    fetchStats(statsParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsKey]);

  // Filter change → reset list to page 1
  useEffect(() => {
    setPage(1);
    fetchList(1, perPage, filterParams);
    // perPage intentionally omitted: a perPage change triggers its own handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const handlePageChange = (p) => {
    setPage(p);
    fetchList(p, perPage, filterParams);
  };
  const handlePerPageChange = (value) => {
    const lim = parseInt(value);
    setPerPage(lim);
    setPage(1);
    fetchList(1, lim, filterParams);
  };

  const columns = useMemo(() => ([
    {
      key: 'talent',
      label: 'Talent',
      wrap: true,
      alwaysVisible: true,
      render: (inst) => <TalentCell inst={inst} />,
    },
    {
      key: 'playbook',
      label: 'Playbook',
      render: (inst) => (
        <span className="text-light-text-primary dark:text-dark-text-primary">{inst.playbook?.name || '—'}</span>
      ),
    },
    {
      key: 'period',
      label: 'Period',
      render: (inst) => (
        <span className="text-light-text-secondary dark:text-dark-text-secondary text-xs whitespace-nowrap">
          {formatDate(inst.started_at)} – {formatDate(inst.ends_at)}
        </span>
      ),
    },
    {
      key: 'progress',
      label: 'Progress',
      width: 200,
      render: (inst) => <ProgressBar percent={inst.progress_percent} isOverdue={inst.is_overdue} />,
    },
    {
      key: 'days_left',
      label: 'Days left',
      render: (inst) => <DaysLeftCell days={daysLeftOf(inst.ends_at)} isOverdue={inst.is_overdue} />,
    },
  ]), []);

  const rowKey = useCallback((inst) => inst.id, []);

  return (
    <div className="animate-fadeIn" translate="no">
      <title>Active Stages | Talent Service</title>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Active Stages</h1>
          <p className="mt-1 text-sm text-light-text-secondary dark:text-dark-text-secondary">
            All ongoing lifecycle stages across talents — onboarding, probation, off-boarding and others.
          </p>
        </div>
        <DataViewToggle value={viewMode} onChange={setViewMode} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Active"               value={stats.active ?? 0}             icon={Activity}      color="blue"   loading={statsLoading} />
        <StatCard title="Overdue"              value={stats.overdue ?? 0}            icon={AlertTriangle} color="red"    loading={statsLoading} />
        <StatCard title="Ending in 7 days"     value={stats.endingSoon ?? 0}         icon={Clock}         color="yellow" loading={statsLoading} />
        <StatCard title="Completed this month" value={stats.completedThisMonth ?? 0} icon={CheckCircle2}  color="green"  loading={statsLoading} />
      </div>

      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Playbook</label>
            <Autocomplete multiple clearable fullWidth options={playbookOptions} value={selectedPlaybookIds} onChange={(values) => setSelectedPlaybookIds(values || [])} placeholder="All playbooks" />
          </div>
          <div>
            <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Manager</label>
            <Autocomplete multiple clearable fullWidth options={managerOptions} value={selectedManagerIds} onChange={(values) => setSelectedManagerIds(values || [])} placeholder="All managers" />
          </div>
          <div>
            <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Talent type</label>
            <Autocomplete
              multiple
              clearable
              fullWidth
              options={TALENT_TYPE_OPTIONS.filter((t) => allowedTalentTypes.includes(t.value))}
              value={selectedTalentTypes}
              onChange={(values) => setSelectedTalentTypes(values || [])}
              placeholder="All types"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <Checkbox
            checked={overdueOnly}
            onChange={(checked) => setOverdueOnly(checked)}
            label="Show only overdue"
            color="primary"
            size="small"
          />
          <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
            {pagination.total} {pagination.total === 1 ? 'result' : 'results'}
          </span>
        </div>
      </div>

      {viewMode === 'cards' && (
        <div className="space-y-4">
          {listLoading ? <div className="py-12 text-center text-light-text-tertiary">Loading active stages…</div> : rows.length ? (
            <ResponsiveCardGrid>
              {rows.map((inst) => {
                const talent = inst.talent || {};
                const fullName = talent.first_name || talent.last_name ? `${talent.last_name || ''} ${talent.first_name || ''}`.trim() : `Talent #${inst.talent_id}`;
                return (
                  <RecordCard key={inst.id}>
                    <RecordCardHeader title={<ProfileLink to={`/talents/${inst.talent_id}#stages`}>{fullName}</ProfileLink>} badge={<DaysLeftCell days={daysLeftOf(inst.ends_at)} isOverdue={inst.is_overdue} />} />
                    <RecordCardFields>
                      <RecordCardField label="Playbook">{inst.playbook?.name || '—'}</RecordCardField>
                      <RecordCardField label="Period">{formatDate(inst.started_at)} – {formatDate(inst.ends_at)}</RecordCardField>
                      <RecordCardField label="Progress" className="sm:col-span-2"><ProgressBar percent={inst.progress_percent} isOverdue={inst.is_overdue} /></RecordCardField>
                    </RecordCardFields>
                  </RecordCard>
                );
              })}
            </ResponsiveCardGrid>
          ) : <div className="py-12 text-center text-light-text-tertiary">No active stages match the filters</div>}
          <div className="overflow-x-auto"><Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} itemsPerPage={pagination.limit} onPageChange={handlePageChange} showResults="range" perPageOptions={PAGINATION_OPTIONS} perPage={perPage} onPerPageChange={handlePerPageChange} showOnSinglePage hasFirstLast /></div>
        </div>
      )}
      {viewMode === 'table' && <Table
        columns={columns}
        data={rows}
        isLoading={listLoading}
        rowKey={rowKey}
        size="medium"
        stickyHeaderOffset={0}
        stickyFooterOffset={0}
        emptyState={
          <div className="py-12 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
            <GitBranch className="mx-auto h-12 w-12 opacity-30 mb-2" />
            <p className="text-sm">No active stages match the filters</p>
          </div>
        }
        footer={
          <Pagination
            currentPage={page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={handlePageChange}
            showResults="range"
            perPageOptions={PAGINATION_OPTIONS}
            perPage={perPage}
            onPerPageChange={handlePerPageChange}
            showOnSinglePage
            hasFirstLast
          />
        }
      />}
    </div>
  );
};

export default StagesDashboard;
